// src/components/superadmin/WhatsAppConfiguration.jsx
// Super admin page for configuring WhatsApp branding per salon
// Follows the PayNowConfiguration pattern: loads all salons, card-based selection, edit form

import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import '../../styles/whatsapp-config.css';

const WhatsAppConfiguration = () => {
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSalon, setSelectedSalon] = useState(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    salonId: '',
    salonName: '',
    displayName: '',
    businessName: '',
    isActive: false
  });

  useEffect(() => {
    loadSalons();
  }, []);

  const loadSalons = async () => {
    try {
      setLoading(true);
      const salonsSnapshot = await getDocs(collection(db, 'salons'));
      const salonsList = [];

      salonsSnapshot.forEach((docSnap) => {
        const salonData = docSnap.data();
        const branding = salonData.whatsappBranding || {};
        salonsList.push({
          salonId: docSnap.id,
          salonName: salonData.name || docSnap.id,
          displayName: branding.displayName || salonData.name || '',
          businessName: branding.businessName || salonData.name || '',
          isActive: branding.isActive || false
        });
      });

      setSalons(salonsList);
    } catch (error) {
      console.error('Error loading WhatsApp configurations:', error);
      setMessage({ type: 'error', text: 'Failed to load salon configurations' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditConfig = async (salonId) => {
    try {
      const salonDoc = await getDoc(doc(db, 'salons', salonId));

      if (salonDoc.exists()) {
        const salonData = salonDoc.data();
        const branding = salonData.whatsappBranding || {};

        setFormData({
          salonId: salonId,
          salonName: salonData.name || salonId,
          displayName: branding.displayName || salonData.name || '',
          businessName: branding.businessName || salonData.name || '',
          isActive: branding.isActive || false
        });

        setSelectedSalon(salonId);
        setShowConfigForm(true);
        setMessage({ type: '', text: '' });
      }
    } catch (error) {
      console.error('Error loading salon WhatsApp config:', error);
      setMessage({ type: 'error', text: 'Failed to load salon configuration' });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      if (!formData.displayName.trim()) {
        setMessage({ type: 'error', text: 'Display Name is required' });
        return;
      }

      const salonRef = doc(db, 'salons', formData.salonId);

      await updateDoc(salonRef, {
        whatsappBranding: {
          displayName: formData.displayName.trim(),
          businessName: formData.businessName.trim() || formData.displayName.trim(),
          isActive: formData.isActive,
          updatedAt: serverTimestamp()
        }
      });

      setMessage({ type: 'success', text: `WhatsApp branding saved for ${formData.salonName}` });

      // Refresh salon list
      await loadSalons();

      setTimeout(() => {
        setShowConfigForm(false);
        setSelectedSalon(null);
        setMessage({ type: '', text: '' });
      }, 2000);

    } catch (error) {
      console.error('Error saving WhatsApp config:', error);
      setMessage({ type: 'error', text: 'Failed to save configuration: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowConfigForm(false);
    setSelectedSalon(null);
    setMessage({ type: '', text: '' });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <div className="whatsapp-config-editor">
        <div className="whatsapp-config-loading">Loading salon configurations...</div>
      </div>
    );
  }

  // Edit form view
  if (showConfigForm) {
    return (
      <div className="whatsapp-config-editor">
        <div className="whatsapp-config-header">
          <div>
            <h2>WhatsApp Branding — {formData.salonName}</h2>
            <p className="whatsapp-config-subtitle">
              Configure how WhatsApp messages appear for this salon's customers.
              All messages are sent from BespokeBeauty's WhatsApp number, with the salon name shown in the message body.
            </p>
          </div>
        </div>

        {message.text && (
          <div className={`whatsapp-config-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="whatsapp-config-form">
          <div className="form-section">
            <h3>Salon Information</h3>

            <div className="form-group">
              <label>Salon ID</label>
              <input type="text" className="form-input" value={formData.salonId} disabled />
            </div>

            <div className="form-group">
              <label>Salon Name</label>
              <input type="text" className="form-input" value={formData.salonName} disabled />
            </div>
          </div>

          <div className="form-section">
            <h3>WhatsApp Branding</h3>
            <p className="form-section-description">
              These values are shown in WhatsApp messages sent to this salon's customers.
            </p>

            <div className="form-group">
              <label htmlFor="displayName">Display Name <span className="required">*</span></label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                placeholder="e.g., Glamour Salon"
                className="form-input"
              />
              <span className="form-help">
                Shown as a prefix in WhatsApp messages: <strong>*[{formData.displayName || 'Salon Name'}]*</strong>
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="businessName">Business Name</label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                placeholder="e.g., Glamour Beauty Pte Ltd"
                className="form-input"
              />
              <span className="form-help">
                Shown in the message footer. Defaults to Display Name if left blank.
              </span>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                <span>Enable WhatsApp messaging for this salon</span>
              </label>
              <span className="form-help">
                When disabled, this salon's admin cannot send WhatsApp campaigns.
              </span>
            </div>
          </div>

          {/* Message Preview */}
          <div className="form-section">
            <h3>Message Preview</h3>
            <div className="whatsapp-message-preview">
              <div className="preview-header">BespokeBeauty</div>
              <div className="preview-body">
                <strong>*[{formData.displayName || 'Salon Name'}]*</strong>
                <br /><br />
                Hi Sarah, your appointment tomorrow at 2pm is confirmed. See you soon!
                <br /><br />
                <em>— {formData.businessName || formData.displayName || 'Salon Name'}</em>
              </div>
            </div>
          </div>

          <div className="whatsapp-config-actions">
            <button
              className="config-btn cancel-btn"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="config-btn save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Salon list view (card-based, like PayNow)
  return (
    <div className="whatsapp-config-editor">
      <div className="whatsapp-config-header">
        <div>
          <h2>WhatsApp Configuration</h2>
          <p className="whatsapp-config-subtitle">
            Configure WhatsApp branding for each salon. All messages are sent from BespokeBeauty's central WhatsApp Business Account.
          </p>
        </div>
      </div>

      {message.text && (
        <div className={`whatsapp-config-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {salons.length === 0 ? (
        <div className="whatsapp-config-loading">
          No salons found. Create a salon first.
        </div>
      ) : (
        <div className="config-cards-grid">
          {salons.map(salon => (
            <div key={salon.salonId} className="config-card">
              <div className="config-card-header">
                <h3>{salon.salonName}</h3>
                <span className={`status-badge ${salon.isActive ? 'active' : 'inactive'}`}>
                  {salon.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="config-card-body">
                <div className="config-field">
                  <label>Display Name:</label>
                  <span>{salon.displayName || '—'}</span>
                </div>
                <div className="config-field">
                  <label>Business Name:</label>
                  <span>{salon.businessName || '—'}</span>
                </div>
                <div className="config-field">
                  <label>Salon ID:</label>
                  <span>{salon.salonId}</span>
                </div>
              </div>
              <div className="config-card-footer">
                <button
                  className="config-btn edit-btn"
                  onClick={() => handleEditConfig(salon.salonId)}
                >
                  Edit Configuration
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WhatsAppConfiguration;
